import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import './MembershipStatusBanner.css';

export function useMyClubMemberships(userId) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!userId) {
      setMemberships([]);
      return [];
    }
    setLoading(true);
    try {
      const data = await api.getMyClubMemberships(userId);
      const list = Array.isArray(data) ? data : [];
      setMemberships(list);
      return list;
    } catch {
      setMemberships([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const pendingMemberships = memberships.filter((m) => m.status === 'PENDING');
  const approvedMemberships = memberships.filter((m) => m.status === 'APPROVED');

  return { memberships, pendingMemberships, approvedMemberships, loading, reload };
}

export function getMembershipStatusForClub(memberships, clubId) {
  const match = memberships.find((m) => m.clubId === clubId);
  return match ? match.status : null;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

const MembershipStatusBanner = ({
  pendingMemberships,
  variant = 'banner',
  showPlayerPendingNote = false,
  dismissible = false,
  onDismiss,
}) => {
  if (!pendingMemberships || pendingMemberships.length === 0) {
    return null;
  }

  const isCompact = variant === 'compact';

  return (
    <div
      className={`membership-status-banner${isCompact ? ' membership-status-banner--compact' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className="membership-status-banner__content">
        <h3 className="membership-status-banner__title">
          {pendingMemberships.length === 1
            ? 'Club membership request pending'
            : `${pendingMemberships.length} club membership requests pending`}
        </h3>
        <ul className="membership-status-banner__list">
          {pendingMemberships.map((m) => (
            <li key={m.id || m.clubId}>
              {m.clubLogoUrl && (
                <img src={m.clubLogoUrl} alt="" className="membership-status-banner__logo" />
              )}
              <span>
                <strong>{m.clubName || 'Club'}</strong>
                {' — '}
                waiting for club admin approval
                {formatDate(m.joinedAt) && (
                  <span className="membership-status-banner__date"> (requested {formatDate(m.joinedAt)})</span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {showPlayerPendingNote && (
          <p className="membership-status-banner__note">
            Your player profile is also pending system admin approval. You will get full access once both
            approvals are complete.
          </p>
        )}
        {!isCompact && (
          <p className="membership-status-banner__hint">
            You do not need to request again. Check status anytime on the{' '}
            <Link to={soccerPath('/clubs/list')}>Clubs</Link> page.
          </p>
        )}
      </div>
      {dismissible && onDismiss && (
        <button type="button" className="membership-status-banner__dismiss" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
};

export default MembershipStatusBanner;
